import { useMemo, useState } from "react";
import { EmptyState, DataAsOf } from "@/components/widgets";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowDownWideNarrow, Award, Lock, ShieldCheck, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { PanelCard, Movement, OutletCell, RankBadge, scopeLockToast } from "./primitives";
import { monthStartISO } from "./constants";
import { StarRating } from "@/components/ratings/StarRating";

export const ClosureLeaderboard = ({ rows, myOutletId, navigate }) => {
  const [sortByRating, setSortByRating] = useState(false);
  const displayRows = useMemo(() => {
    if (!sortByRating) return rows;
    return [...rows].sort((a, b) =>
      ((b.google_rating ?? -1) - (a.google_rating ?? -1)) ||
      ((b.google_new_mtd ?? 0) - (a.google_new_mtd ?? 0)));
  }, [rows, sortByRating]);
  return (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-[hsl(var(--status-green))]" />Closure Leaderboard — all locations</span>}
    right={<DataAsOf />}
    testId="closure-leaderboard-panel"
    className="col-span-2 order-[5.1] lg:order-none lg:col-span-6">
    {rows.length === 0 ? (
      <EmptyState title="No closures yet this month" />
    ) : (
      <div className="overflow-x-auto">
        <Table className="cx-table min-w-[620px]" data-testid="closure-leaderboard-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">#</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead className="text-right">Genuine</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Open</TableHead>
              <TableHead className="text-right">Reopens</TableHead>
              <TableHead className="text-right">
                <button
                  className={cn("inline-flex items-center gap-1 hover:text-foreground transition-colors",
                    sortByRating && "text-[hsl(var(--cx-gold))] font-semibold")}
                  onClick={() => setSortByRating((v) => !v)}
                  title="Sort by Google rating"
                  data-testid="closure-lb-sort-rating">
                  G-Rating <ArrowDownWideNarrow size={12} />
                </button>
              </TableHead>
              <TableHead className="text-center pr-4">Move</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((o) => {
              const locked = o.in_scope === false;
              return (
              <TableRow key={o.outlet_id}
                className={cn(locked ? "cursor-default" : "cursor-pointer",
                  o.outlet_id === myOutletId && "bg-[hsl(var(--accent))] border-l-2 border-l-[hsl(var(--cx-gold))]")}
                data-locked={locked ? "true" : undefined}
                data-testid={`closure-lb-row-${o.outlet_code}`}
                onClick={() => locked
                  ? scopeLockToast()
                  : navigate(`/complaints?outlet_id=${o.outlet_id}&closed_from=${monthStartISO()}`)}>
                <TableCell className="pl-4 tabular">{o.rank}</TableCell>
                <OutletCell o={o} myOutletId={myOutletId} locked={locked} />
                <TableCell className="text-right tabular font-semibold">{o.genuine_closures}</TableCell>
                <TableCell className="text-right tabular font-semibold">{o.genuine_rate}%</TableCell>
                <TableCell className="text-right tabular" data-testid={`closure-lb-open-${o.outlet_code}`}>{o.open_now}</TableCell>
                <TableCell className="text-right tabular">
                  <span className={cn(o.reopen_warning && "text-[hsl(var(--cx-crimson))] font-semibold")}>
                    {o.reopens}{o.reopen_warning && " ⚠"}
                  </span>
                </TableCell>
                <TableCell className="text-right" data-testid={`closure-lb-grating-${o.outlet_code}`}>
                  {o.google_rating !== null && o.google_rating !== undefined ? (
                    <span className="inline-flex flex-col items-end">
                      <span className="inline-flex items-center gap-1 tabular font-semibold">
                        {o.google_rating.toFixed(1)} <StarRating value={o.google_rating} size={10} />
                      </span>
                      <span className="text-[0.6rem] text-muted-foreground tabular">+{o.google_new_mtd ?? 0} MTD</span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-center pr-4"><Movement movement={o.movement} /></TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <p className="px-5 pt-1.5 pb-2 text-[0.65rem] text-muted-foreground">
          Group-wide standings — rows with <Lock size={10} className="inline -mt-0.5" /> open only for that location's team and leadership.
        </p>
      </div>
    )}
  </PanelCard>
  );
};

export const PreventionLeaderboard = ({ prevention, myOutletId, navigate }) => (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-[hsl(var(--cx-navy))]" />Prevention Leaderboard — fewest new complaints</span>}
    right={<DataAsOf />}
    testId="prevention-leaderboard-panel"
    className="col-span-2 order-[5.2] lg:order-none lg:col-span-6">
    {prevention.items.length === 0 ? (
      <EmptyState title="No outlets in scope" />
    ) : (
      <div className="overflow-x-auto">
        <Table className="cx-table min-w-[440px]" data-testid="prevention-leaderboard-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">#</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead className="text-right">Opened</TableHead>
              {prevention.throughput_mode && <TableHead className="text-right">/100 veh</TableHead>}
              <TableHead className="text-right">Last Month</TableHead>
              <TableHead className="text-center pr-4">Move</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prevention.items.map((o) => {
              const locked = o.in_scope === false;
              return (
              <TableRow key={o.outlet_id}
                className={cn(locked ? "cursor-default" : "cursor-pointer",
                  o.outlet_id === myOutletId && "bg-[hsl(var(--accent))] border-l-2 border-l-[hsl(var(--cx-gold))]")}
                data-locked={locked ? "true" : undefined}
                data-testid={`prevention-lb-row-${o.outlet_code}`}
                onClick={() => locked
                  ? scopeLockToast()
                  : navigate(`/complaints?outlet_id=${o.outlet_id}&date_from=${monthStartISO()}`)}>
                <TableCell className="pl-4 tabular">{o.rank}</TableCell>
                <OutletCell o={o} myOutletId={myOutletId} locked={locked} />
                <TableCell className="text-right tabular font-semibold">{o.opened}</TableCell>
                {prevention.throughput_mode && <TableCell className="text-right tabular">{o.per_100 ?? "—"}</TableCell>}
                <TableCell className="text-right tabular text-muted-foreground">{o.opened_prev}</TableCell>
                <TableCell className="text-center pr-4"><Movement movement={o.movement} /></TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    )}
  </PanelCard>
);

const HandlerRows = ({ rows, navigate, showOutlet, testPrefix }) => (
  <TableBody>
    {rows.map((h) => {
      const locked = h.in_scope === false || !h.handler;
      return (
        <TableRow key={`${testPrefix}-${h.rank}-${h.display_name || h.handler}`}
          className={locked ? "cursor-default" : "cursor-pointer"}
          data-locked={locked ? "true" : undefined}
          data-testid={`${testPrefix}-row-${h.rank}`}
          onClick={() => locked
            ? scopeLockToast("Complaint lists for other teams are visible to their managers and leadership.")
            : navigate(`/complaints?search=${encodeURIComponent(h.handler)}`)}>
          <TableCell className="pl-4 tabular"><RankBadge rank={h.rank} /></TableCell>
          <TableCell className="font-medium max-w-[220px] truncate">
            <span className={cn(locked && "text-muted-foreground")}>{h.display_name || h.handler}</span>
            {locked && <Lock size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground/70" aria-label="details locked" />}
          </TableCell>
          {showOutlet && (
            <TableCell className="max-w-[200px] truncate">
              {h.outlet_name
                ? <><span>{h.outlet_name}</span> <span className="text-xs text-muted-foreground">{h.outlet_code}</span></>
                : <span className="text-xs text-muted-foreground">—</span>}
            </TableCell>
          )}
          <TableCell className="text-right tabular font-semibold">{h.genuine_closures}</TableCell>
          <TableCell className="text-right tabular">{h.closed}</TableCell>
          <TableCell className="text-right tabular">{h.open_now}</TableCell>
          <TableCell className="text-right tabular pr-4">
            <span className={cn(h.reopen_warning && "text-[hsl(var(--cx-crimson))] font-semibold")}>
              {h.reopens}{h.reopen_warning && " ⚠"}
            </span>
          </TableCell>
        </TableRow>
      );
    })}
  </TableBody>
);

export const HandlerLeaderboard = ({ standings, navigate }) => {
  const handlers = standings?.handlers || [];
  const local = standings?.handlers_local || [];
  const viewerRank = standings?.viewer_rank;
  const total = standings?.handlers_total || 0;
  return (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><Award size={15} className="text-[hsl(var(--cx-gold))]" />Best Performers — Genuine Closures This Month</span>}
    right={<DataAsOf />}
    testId="handler-leaderboard-panel"
    className="col-span-2 order-[5.3] lg:order-none lg:col-span-12">
    {handlers.length === 0 ? (
      <EmptyState title="No closures by team members yet this month" />
    ) : (
      <div className="overflow-x-auto">
        <p className="px-5 pb-2 text-[0.68rem] text-muted-foreground">
          Group-wide top 10 across every location — a closure only counts while it stays closed; reopened cases lose their credit.
          {total > 10 && ` ${total} team members ranked this month.`} Names outside your scope are shortened for privacy.
        </p>
        {viewerRank && (
          <p className="mx-5 mb-2 px-3 py-1.5 rounded-md text-xs font-medium bg-[hsl(var(--cx-gold)/0.08)] border border-[hsl(var(--cx-gold)/0.35)] text-[hsl(var(--cx-ink))]"
            data-testid="handler-viewer-rank">
            Your rank: <span className="font-bold tabular">{viewerRank.rank} of {viewerRank.total}</span>
            {" "}· {viewerRank.genuine_closures} genuine closure{viewerRank.genuine_closures !== 1 ? "s" : ""} this month
          </p>
        )}
        <Table className="cx-table min-w-[720px]" data-testid="handler-leaderboard-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">#</TableHead>
              <TableHead>Team Member</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead className="text-right">Genuine Closures</TableHead>
              <TableHead className="text-right">Closed</TableHead>
              <TableHead className="text-right">Open Now</TableHead>
              <TableHead className="text-right pr-4">Reopens</TableHead>
            </TableRow>
          </TableHeader>
          <HandlerRows rows={handlers} navigate={navigate} showOutlet testPrefix="handler-lb" />
        </Table>

        {local.length > 0 && (
          <div className="mt-3 border-t pt-2" data-testid="handler-local-section">
            <p className="px-5 pb-2 text-[0.68rem] font-medium text-[hsl(var(--cx-ink))] flex items-center gap-1.5">
              <Users size={13} className="text-[hsl(var(--cx-navy))]" /> Your outlet's team — full detail
            </p>
            <Table className="cx-table min-w-[620px]" data-testid="handler-local-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">#</TableHead>
                  <TableHead>Team Member</TableHead>
                  <TableHead className="text-right">Genuine Closures</TableHead>
                  <TableHead className="text-right">Closed</TableHead>
                  <TableHead className="text-right">Open Now</TableHead>
                  <TableHead className="text-right pr-4">Reopens</TableHead>
                </TableRow>
              </TableHeader>
              <HandlerRows rows={local} navigate={navigate} showOutlet={false} testPrefix="handler-local" />
            </Table>
          </div>
        )}
      </div>
    )}
  </PanelCard>
  );
};

export const SaTable = ({ rows, navigate }) => (
  rows?.length ? (
    <Table className="cx-table">
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Team Member</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead className="text-right">Open</TableHead>
          <TableHead className="text-right">Red (5+ d)</TableHead>
          <TableHead className="text-right pr-4">Avg d</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((s) => (
          <TableRow key={s.sa} className="cursor-pointer" data-testid={`sa-row-${s.sa}`}
            onClick={() => navigate(`/complaints?search=${encodeURIComponent(s.sa)}&open=1`)}>
            <TableCell className="pl-4 max-w-[220px] truncate">{s.sa}</TableCell>
            <TableCell className="text-right tabular">{s.total}</TableCell>
            <TableCell className="text-right tabular">{s.open}</TableCell>
            <TableCell className="text-right tabular text-[hsl(var(--cx-crimson))] font-semibold">{s.red}</TableCell>
            <TableCell className="text-right tabular pr-4">{s.avg_resolution_days}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  ) : (
    <EmptyState title="No advisor data" />
  )
);
